// File: src/components/Settings/NotificationPreferences.jsx
// Simplified notification preferences section for Settings page

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  FormLayout,
  TextField,
  Select,
  Button,
  Banner,
  Text,
  Checkbox,
  BlockStack,
  InlineStack,
  Box,
  Spinner,
} from '@shopify/polaris';
import useData from '../../hooks/useData';

export default function NotificationPreferences({ token }) {
  const API = process.env.REACT_APP_API_URL;
  
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [prefs, setPrefs] = useState({
    email: '',
    email_enabled: true,
    digest_frequency: 'daily',
    critical_only: false,
    timezone: 'America/Phoenix',
  });

  // Fetch current preferences (uses /api/notifications/preferences from notify.py)
  const { data: currentPrefs, loading } = useData(
    'notification-prefs',
    () => fetch(`${API}/api/notifications/preferences`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.ok ? r.json() : null),
    { ttlMs: 0 }
  );

  useEffect(() => {
    if (currentPrefs) {
      setPrefs(p => ({
        ...p,
        email: currentPrefs.email || '',
        email_enabled: currentPrefs.email_enabled ?? true,
        digest_frequency: currentPrefs.digest_frequency || 'daily',
        critical_only: currentPrefs.critical_only ?? false,
        timezone: currentPrefs.timezone || 'America/Phoenix',
      }));
    }
  }, [currentPrefs]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setErrorMessage('');
    setSuccessMessage('');

    // Basic email validation
    if (prefs.email_enabled && !prefs.email) {
      setErrorMessage('Please enter your email address to receive notifications.');
      setSaving(false);
      return;
    }

    try {
      const response = await fetch(`${API}/api/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(prefs),
      });

      if (response.ok) {
        setSuccessMessage('Notification preferences saved!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const data = await response.json();
        setErrorMessage(data.detail || 'Failed to save preferences');
      }
    } catch (error) {
      setErrorMessage('Error saving preferences: ' + error.message);
    } finally {
      setSaving(false);
    }
  }, [API, token, prefs]);

  const frequencyOptions = [
    { label: 'Daily (12pm)', value: 'daily' },
    { label: 'Weekly (Monday)', value: 'weekly' },
    { label: 'Immediate (Critical only)', value: 'immediate' },
    { label: 'Never', value: 'never' },
  ];

  const timezoneOptions = [
    { label: 'US/Pacific (PT)', value: 'America/Los_Angeles' },
    { label: 'US/Mountain (MST)', value: 'America/Phoenix' },
    { label: 'US/Central (CT)', value: 'America/Chicago' },
    { label: 'US/Eastern (ET)', value: 'America/New_York' },
    { label: 'UTC', value: 'UTC' },
  ];

  if (loading) {
    return (
      <Card>
        <Box padding="400">
          <InlineStack align="center" gap="200">
            <Spinner size="small" />
            <Text>Loading preferences...</Text>
          </InlineStack>
        </Box>
      </Card>
    );
  }

  return (
    <Card>
      <BlockStack gap="400">
        <Text variant="headingMd" as="h2">Email Notifications</Text>
        <Text tone="subdued">
          Get prescription recommendations and alerts delivered to your inbox.
        </Text>

        {successMessage && (
          <Banner tone="success" onDismiss={() => setSuccessMessage('')}>
            {successMessage}
          </Banner>
        )}

        {errorMessage && (
          <Banner tone="critical" onDismiss={() => setErrorMessage('')}>
            {errorMessage}
          </Banner>
        )}

        {/* Show prompt if no email is set */}
        {!prefs.email && prefs.email_enabled && (
          <Banner tone="warning">
            <p>Enter your email address to receive BaoBrain recommendations and alerts.</p>
          </Banner>
        )}

        {/* Show confirmation when we have their email from Shopify */}
        {prefs.email && currentPrefs?.email === prefs.email && (
          <Banner tone="info">
            <p>We detected your email from your account. You can change it below if needed.</p>
          </Banner>
        )}

        <FormLayout>
          <TextField
            label="Email Address"
            type="email"
            value={prefs.email}
            onChange={(value) => setPrefs(p => ({ ...p, email: value }))}
            placeholder="your@email.com"
            helpText="We'll send prescription recommendations to this address"
            autoComplete="email"
          />

          <Checkbox
            label="Enable email notifications"
            checked={prefs.email_enabled}
            onChange={(checked) => setPrefs(p => ({ ...p, email_enabled: checked }))}
          />

          {prefs.email_enabled && (
            <>
              <Select
                label="How often?"
                options={frequencyOptions}
                value={prefs.digest_frequency}
                onChange={(value) => setPrefs(p => ({ ...p, digest_frequency: value }))}
              />

              <Select
                label="Timezone"
                options={timezoneOptions}
                value={prefs.timezone}
                onChange={(value) => setPrefs(p => ({ ...p, timezone: value }))}
              />

              <Checkbox
                label="Critical alerts only"
                helpText="Only notify me about urgent issues that need immediate attention"
                checked={prefs.critical_only}
                onChange={(checked) => setPrefs(p => ({ ...p, critical_only: checked }))}
              />
            </>
          )}
        </FormLayout>

        <InlineStack align="end">
          <Button variant="primary" onClick={handleSave} loading={saving}>
            Save Preferences
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}